import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storageService } from '../services/storage';

interface SelectedRestaurantContextProps {
  selectedRestaurantId: string;
  selectedRestaurantName: string;
  selectedRestaurantLogo: string | null;
  selectedRestaurantStatus: 'open' | 'closed' | null;
  selectedRestaurantTimezone: { name: string; offset: number } | null;
  setSelectedRestaurant: (id: string, name: string, logo?: string, status?: 'open' | 'closed', timezone?: { name: string; offset: number } | null) => Promise<void>;
  setSelectedRestaurantTimezone: (timezone: { name: string; offset: number } | null) => Promise<void>;
  clearSelectedRestaurant: () => Promise<void>;
  isLoading: boolean;
}

const SelectedRestaurantContext = createContext<SelectedRestaurantContextProps | undefined>(undefined);

export const SelectedRestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [selectedRestaurantName, setSelectedRestaurantName] = useState('');
  const [selectedRestaurantLogo, setSelectedRestaurantLogo] = useState<string | null>(null);
  const [selectedRestaurantStatus, setSelectedRestaurantStatus] = useState<'open' | 'closed' | null>(null);
  const [selectedRestaurantTimezone, setSelectedRestaurantTimezoneState] = useState<{ name: string; offset: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { id, name, logo, status, timezone } = await storageService.getSelectedRestaurant();
        if (id) setSelectedRestaurantId(id);
        if (name) setSelectedRestaurantName(name);
        if (logo) setSelectedRestaurantLogo(logo);
        if (status) setSelectedRestaurantStatus(status);
        if (timezone) setSelectedRestaurantTimezoneState(timezone);
      } catch (error) {
        console.error('Error cargando restaurante seleccionado:', error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const setSelectedRestaurant = useCallback(async (id: string, name: string, logo?: string, status?: 'open' | 'closed', timezone?: { name: string; offset: number } | null) => {
    try {
      await storageService.saveSelectedRestaurant(id, name, logo, status, timezone ?? selectedRestaurantTimezone);
      setSelectedRestaurantId(id);
      setSelectedRestaurantName(name);
      setSelectedRestaurantLogo(logo || null);
      if (status) setSelectedRestaurantStatus(status);
      if (timezone) setSelectedRestaurantTimezoneState(timezone);
    } catch (error) {
      console.error('Error guardando restaurante seleccionado:', error);
    }
  }, [selectedRestaurantTimezone]);

  const setSelectedRestaurantTimezone = useCallback(async (timezone: { name: string; offset: number } | null) => {
    try {
      await storageService.saveSelectedRestaurantTimezone(timezone);
      setSelectedRestaurantTimezoneState(timezone);
    } catch (error) {
      console.error('Error guardando timezone del restaurante seleccionado:', error);
    }
  }, []);

  const clearSelectedRestaurant = useCallback(async () => {
    try {
      await storageService.clearSelectedRestaurant();
      setSelectedRestaurantId('');
      setSelectedRestaurantName('');
      setSelectedRestaurantLogo(null);
      setSelectedRestaurantStatus(null);
      setSelectedRestaurantTimezoneState(null);
    } catch (error) {
      console.error('Error limpiando restaurante seleccionado:', error);
    }
  }, []);

  return (
    <SelectedRestaurantContext.Provider value={{
      selectedRestaurantId,
      selectedRestaurantName,
      selectedRestaurantLogo,
      selectedRestaurantStatus,
      setSelectedRestaurant,
      selectedRestaurantTimezone,
      setSelectedRestaurantTimezone,
      clearSelectedRestaurant,
      isLoading
    }}>
      {children}
    </SelectedRestaurantContext.Provider>
  );
};

export const useSelectedRestaurant = () => {
  const ctx = useContext(SelectedRestaurantContext);
  if (!ctx) throw new Error('useSelectedRestaurant debe usarse dentro de SelectedRestaurantProvider');
  return ctx;
}; 